import React from "react";
import classnames from "classnames";
import Select from "react-select";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  Label,
  FormGroup,
  InputGroup,
  InputGroupText,
  Form,
  Row,
  Input,
  Badge,
} from "reactstrap";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import { ToastContainer } from "react-toastify";
import TableWrapper from "@/utils/TableWrapper";

/**
 * The parent options and the property rows are the container's `any[]` state
 * (AddCategorieContainer.tsx); this view only renders them.
 */
export type AddCategorieViewProps = {
  validation: {
    name: boolean;
    slug: boolean;
    code: boolean;
    codeMessage: string;
    parentcategorie: boolean | null;
  };
  name: string;
  slug: string;
  code?: string;
  parentCategorie: unknown;
  selectParentOptions: any[];
  showProps: boolean;
  hostsProducts?: boolean;
  propertiesTable: any[];
  selectedBadges: string[];
  countryCode?: string;
  onSelectParent: (opt: unknown) => void;
  onChangeName: React.ChangeEventHandler<HTMLInputElement>;
  onChangeSlug: React.ChangeEventHandler<HTMLInputElement>;
  onChangeCode: React.ChangeEventHandler<HTMLInputElement>;
  onChangeHostsProducts: React.ChangeEventHandler<HTMLInputElement>;
  onToggleProperty: (row: unknown) => void;
  onSubmit: (e?: React.SyntheticEvent) => void;
  canWrite: boolean;
};

const AddCategorieView = ({
  // state
  validation,
  name,
  slug,
  code = "",
  parentCategorie,
  selectParentOptions,
  showProps,
  hostsProducts = false,
  propertiesTable,
  selectedBadges,
  countryCode,
  // handlers
  onSelectParent,
  onChangeName,
  onChangeSlug,
  onChangeCode,
  onChangeHostsProducts,
  onToggleProperty,
  onSubmit,
  // perms
  canWrite,
}: AddCategorieViewProps) => {
  const columns = [
    { accessorKey: "name", header: "Name" },
    {
      accessorFn: (row) => row.label?.[0]?.value?.label,
      header: "Label",
    },
    { accessorKey: "description", header: "Description" },
    { accessorKey: "type", header: "Type" },
    {
      header: "ACTION",
      cell: ({ row }) => (
        <label className="custom-toggle">
          <input
            defaultChecked={row.original.added}
            type="checkbox"
            onChange={() => onToggleProperty(row.original)}
          />
          <span
            className="custom-toggle-slider rounded-circle"
            data-label-off="disable"
            data-label-on="enable"
          />
        </label>
      ),
    },
  ];

  return (
    <>
      {/* NOTIFICATIONS */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <SimpleHeader name="Add Category" parentName="Categories" />
      <Container className="mt--6" fluid>
        <Card>
          <CardHeader>
            <Row className="align-items-center">
              <Col xs="8">
                <h5 className="h3 mb-0">Add Category</h5>
              </Col>
              {!canWrite ? (
                <Col className="text-right" xs="4">
                  <h4 className="h4 mb-0 text-danger">Add Permission Denied</h4>
                </Col>
              ) : null}
            </Row>
          </CardHeader>

          <CardBody>
            <Form role="form" onSubmit={onSubmit}>
              <FormGroup className="row">
                <Label className="form-control-label" md="2">
                  Country
                </Label>
                <Col md="8">
                  <InputGroup className={classnames("input-group-merge")}>
                    <InputGroupText>
                      <i className="ni ni-world-2" />
                    </InputGroupText>
                    <Input
                      type="text"
                      value={countryCode || ""}
                      readOnly
                      disabled
                    />
                  </InputGroup>
                </Col>
              </FormGroup>

              {/* Category Name */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">
                  New Category Name
                </Label>
                <Col md="8">
                  <InputGroup className={classnames("input-group-merge")}>
                    <InputGroupText>
                      <i className="ni ni-bullet-list-67" />
                    </InputGroupText>
                    <Input
                      placeholder="Insert New Category"
                      invalid={!validation.name}
                      name="name"
                      type="text"
                      value={name}
                      onChange={onChangeName}
                    />
                  </InputGroup>
                  {!validation.name ? (
                    <InputGroup className="invalid-feedback">
                      Required State.
                    </InputGroup>
                  ) : null}
                </Col>
              </FormGroup>

              {/* SCRUM-1705: the node's permanent identity (SCRUM-1673). Required
                  on both create routes; pre-filled from the name. */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2" for="add-category-code">
                  Code
                </Label>
                <Col md="8">
                  <InputGroup className={classnames("input-group-merge")}>
                    <InputGroupText>
                      <i className="ni ni-key-25" />
                    </InputGroupText>
                    <Input
                      id="add-category-code"
                      placeholder="lower-case letters, digits and underscores"
                      invalid={!validation.code}
                      name="code"
                      type="text"
                      value={code}
                      onChange={onChangeCode}
                      maxLength={64}
                      required
                    />
                  </InputGroup>
                  <small className="form-text text-muted">
                    The node&apos;s permanent identity — the migration and the URL
                    rules resolve on it; it cannot be changed after creation.
                  </small>
                  {!validation.code ? (
                    <InputGroup className="invalid-feedback d-block">
                      {validation.codeMessage ||
                        "Required: lower-case letters, digits and underscores."}
                    </InputGroup>
                  ) : null}
                </Col>
              </FormGroup>

              {/* (Optional) Slug field — kept commented like original */}
              {/* 
              <FormGroup className="row">
                <Label className="form-control-label" md="2">
                  Slug
                </Label>
                <Col md="8">
                  <InputGroup className={classnames("input-group-merge")}>
                    <InputGroupText>
                      <i className="ni ni-world-2" />
                    </InputGroupText>
                    <Input
                      placeholder="Url-friendly Version of the Name"
                      invalid={!validation.slug}
                      name="slug"
                      type="text"
                      value={slug}
                      onChange={onChangeSlug}
                    />
                  </InputGroup>
                  {!validation.slug ? (
                    <InputGroup className="invalid-feedback">
                      Required State.
                    </InputGroup>
                  ) : null}
                </Col>
              </FormGroup>
              */}

              {/* Parent Category */}
              <FormGroup className="row">
                <Label className="form-control-label" md="2">
                  Parent Category
                </Label>
                <Col md="8">
                  <Select
                    name="parentcategorie"
                    options={selectParentOptions}
                    onChange={onSelectParent}
                    isClearable
                    isSearchable
                    // react-select does not support 'invalid' directly; validation is shown below
                  />
                  {validation.parentcategorie ? (
                    <InputGroup className="invalid-feedback">
                      Required State.
                    </InputGroup>
                  ) : null}
                </Col>
              </FormGroup>
            </Form>

            {/* Properties Section when parent != None */}
            {showProps ? (
              <>
                {/* SCRUM-1668: the trunk flag, only meaningful on a subcategory */}
                <Col md="12" className="mb-3">
                  <label className="custom-toggle">
                    <input
                      type="checkbox"
                      name="hostsProducts"
                      checked={hostsProducts}
                      onChange={onChangeHostsProducts}
                      aria-label="Hosts products (trunk)"
                    />
                    <span className="custom-toggle-slider rounded-circle" />
                  </label>
                  <span className="ml-2">Hosts products (trunk)</span>
                </Col>

                <Label className="form-control-label" md="2">
                  Add Properties
                </Label>

                {/* Selected badges */}
                <Col md="8">
                  {selectedBadges.map((label, idx) => (
                    <Badge color="success" pill key={`${label}-${idx}`}>
                      {label}
                    </Badge>
                  ))}
                </Col>

                <Col md="12">
                  <TableWrapper data={propertiesTable} columns={columns} />
                </Col>
              </>
            ) : null}

            {/* Footer actions */}
            <Col md="10" className="text-right">
              <Col>
                <Button
                  className="my-0"
                  color="btn btn-outline-default"
                  to="/admin/category/update"
                  tag={Link}
                >
                  Back To Categories
                </Button>
                {canWrite ? (
                  <Button
                    className="my-0"
                    color="btn btn-outline-primary"
                    onClick={onSubmit}
                  >
                    Add
                  </Button>
                ) : null}
              </Col>
            </Col>
          </CardBody>
        </Card>
      </Container>
    </>
  );
};

export default React.memo(AddCategorieView);
